// Auth Module - Vuex Store
import { loginWithEmailAndPassword, logout as authLogout, getIdToken } from '@/services/authService'
import { getUserByUID, validateUserStatusAndRole } from '@/services/usersService'
import { startUserSessionWatch, stopUserSessionWatch } from '@/services/sessionWatchService'
import { getUserAccessMessage } from '@/utils/sessionGuard'

let forceLogoutInProgress = false

const state = {
  user: null,
  token: null,
  loading: false,
  error: null,
}

const getters = {
  isAuthenticated: state => {
    return !!state.user
  },
  currentUser: state => {
    return state.user
  },
  userRole: state => {
    return state.user ? state.user.rol : null
  },
  isLoading: state => {
    return state.loading
  },
  authError: state => {
    return state.error
  },
}

const mutations = {
  setUser(state, user) {
    state.user = user
    // Guardar en localStorage
    if (user) {
      localStorage.setItem('auth_user', JSON.stringify(user))
    } else {
      localStorage.removeItem('auth_user')
    }
  },
  setToken(state, token) {
    state.token = token
    if (token) {
      localStorage.setItem('auth_token', token)
    } else {
      localStorage.removeItem('auth_token')
    }
  },
  setLoading(state, loading) {
    state.loading = loading
  },
  setError(state, error) {
    state.error = error
  },
  logout(state) {
    state.user = null
    state.token = null
    state.error = null
    localStorage.removeItem('auth_user')
    localStorage.removeItem('auth_token')
  },
  syncUserFromRTDB(state, userData) {
    if (!state.user || !userData) return

    state.user = {
      ...state.user,
      email: userData.email || state.user.email,
      name: userData.name || state.user.name,
      rol: userData.rol || state.user.rol,
      status: userData.status || state.user.status,
      defaultStore: userData.defaultStore ?? state.user.defaultStore,
    }

    localStorage.setItem('auth_user', JSON.stringify(state.user))
  },
}

const actions = {
  /**
   * Login action
   * @param {Object} context - Vuex context
   * @param {Object} payload - { email, password }
   */
  async login({ commit, dispatch }, { email, password }) {
    commit('setLoading', true)
    commit('setError', null)

    try {
      // 1. Autenticar con Firebase
      const authResult = await loginWithEmailAndPassword(email, password)

      if (!authResult.success) {
        commit('setLoading', false)
        commit('setError', authResult.error)
        return {
          success: false,
          error: authResult.error,
        }
      }

      const uid = authResult.uid

      // 2. Obtener datos del usuario desde Realtime Database
      const userResult = await getUserByUID(uid)

      if (!userResult.success || !userResult.data) {
        console.warn('[auth/login] user not in RTDB', { uid, detail: userResult.error })
        const userMessage = getUserAccessMessage('not_found')
        commit('setLoading', false)
        commit('setError', userMessage)
        await authLogout()
        return {
          success: false,
          error: userMessage,
        }
      }

      const userData = userResult.data

      // 3. Validar status y rol
      const validation = validateUserStatusAndRole(userData)

      if (!validation.valid) {
        if (validation.internalDetail) {
          console.warn('[auth/login] access denied:', validation.reason, validation.internalDetail)
        }
        const userMessage = getUserAccessMessage(validation.reason)
        commit('setLoading', false)
        commit('setError', userMessage)
        // Cerrar sesión de Firebase si la validación falla
        await authLogout()
        return {
          success: false,
          error: userMessage,
        }
      }

      // 4. Sincronizar hashedPassword si es necesario (después de reset de contraseña)
      // Si el hashedPassword está vacío o no coincide con la contraseña actual,
      // actualizarlo automáticamente
      const { syncPasswordHash } = await import('@/services/usersService')
      await syncPasswordHash(uid, password, userData.hashedPassword)

      // 5. Guardar usuario en store y localStorage
      const user = {
        uid: uid,
        email: userData.email || email,
        name: userData.name || '',
        rol: userData.rol,
        status: userData.status,
        defaultStore: userData.defaultStore || null,
      }

      // Obtener token de Firebase
      const token = await getIdToken()

      commit('setUser', user)
      commit('setToken', token || uid)

      commit('setLoading', false)

      dispatch('startSessionWatch')

      return {
        success: true,
        user: user,
      }
    } catch (error) {
      console.error('Error in login action:', error)
      commit('setLoading', false)
      commit('setError', error.message || 'Error inesperado al iniciar sesión')
      return {
        success: false,
        error: error.message || 'Error inesperado al iniciar sesión',
      }
    }
  },

  /**
   * Logout action
   * @param {Object} context - Vuex context
   */
  async logout({ commit }) {
    try {
      commit('setLoading', true)

      stopUserSessionWatch()

      // Cerrar sesión en Firebase
      await authLogout()

      // Limpiar store
      commit('logout')
      commit('setLoading', false)

      return {
        success: true,
      }
    } catch (error) {
      console.error('Error in logout action:', error)
      // Aunque haya error, limpiar el store local
      stopUserSessionWatch()
      commit('logout')
      commit('setLoading', false)
      return {
        success: false,
        error: error.message || 'Error al cerrar sesión',
      }
    }
  },

  /**
   * Cierra sesión forzada cuando RTDB indica usuario eliminado, inactivo o inválido.
   * @param {Object} context
   * @param {{ reason?: string, internalDetail?: string, redirect?: boolean }} payload
   */
  async forceLogout({ commit, dispatch }, payload = {}) {
    if (forceLogoutInProgress) {
      return { success: true, skipped: true }
    }

    forceLogoutInProgress = true
    const { reason, internalDetail, redirect = true } = payload

    try {
      stopUserSessionWatch()

      if (internalDetail) {
        console.warn('[auth/forceLogout]', reason, internalDetail)
      }

      const message = getUserAccessMessage(reason)
      sessionStorage.setItem('auth_logout_message', message)
      if (reason) {
        sessionStorage.setItem('auth_logout_reason', reason)
      }

      try {
        await authLogout()
      } catch (authError) {
        console.error('Error during forced auth logout:', authError)
      }

      commit('logout')

      if (redirect) {
        const { default: router } = await import('@/router')
        if (router.currentRoute.value.path !== '/login') {
          await router.push({
            path: '/login',
            query: reason ? { reason } : undefined,
          })
        }
      }

      return { success: true, message }
    } finally {
      forceLogoutInProgress = false
    }
  },

  /**
   * Escucha en tiempo real el nodo RTDB del usuario autenticado.
   */
  startSessionWatch({ state, dispatch }) {
    stopUserSessionWatch()

    const uid = state.user?.uid
    if (!uid) {
      return
    }

    startUserSessionWatch(uid, async evaluation => {
      if (evaluation.valid) {
        return
      }

      await dispatch('forceLogout', {
        reason: evaluation.reason,
        internalDetail: evaluation.internalDetail,
      })
    })
  },

  stopSessionWatch() {
    stopUserSessionWatch()
  },

  /**
   * Initialize auth state from localStorage
   * @param {Object} context - Vuex context
   */
  initAuth({ commit, dispatch }) {
    const storedUser = localStorage.getItem('auth_user')
    const storedToken = localStorage.getItem('auth_token')

    if (storedUser) {
      try {
        const user = JSON.parse(storedUser)
        commit('setUser', user)
        if (storedToken) {
          commit('setToken', storedToken)
        }
        dispatch('startSessionWatch')
      } catch (error) {
        console.error('Error parsing stored user:', error)
        commit('logout')
      }
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
